import React, { useState, useEffect, useMemo } from 'react';
import _, { clone } from 'lodash';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';
import { Input, PrimaryButton, Select, Switch } from '@components/atoms';
import { dialogContentsState } from '@components/templates/Dialog';
import { getFetcher, PRODUCT_CODE } from '../../../../App';
import useStaticDialog from '../../../../utils/hooks/useStaticDialog';
import SettingCommonMethods from '../../../../utils/settingCommon';
import {
  CheckboxLabel,
  InputList,
  ReferenceBox,
  SettingBox,
  SettingList,
  SettingWrapper,
  StyledImageSettingBox,
  StyledReportWrap,
  SwButtonSection,
  SwitchBox,
} from './style';
import { submitDebounce } from 'utils/lib';
import { useCatchLeave } from 'utils/hooks';

interface sendToPacsState {
  isCrNmOverlay: boolean;
  isRelayCrImage: boolean;
  isRelayImage: boolean;
  isRelayLongitudinalReport: boolean;
  isRelayMrImage: boolean;
  isRelayPacsReport: boolean;
  isRelaySingleReport: boolean;
  region: string;
}

const NMSendToPacs = () => {
  const { submitConfirmDialog, confirmTurnAutomaticallyOnOffDialog } = useStaticDialog();
  const { errorHandler } = SettingCommonMethods();
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const [data, setData] = useState<sendToPacsState>({
    region: '',
    isCrNmOverlay: true,
    isRelayCrImage: true,
    isRelayImage: true,
    isRelayLongitudinalReport: true,
    isRelayMrImage: true,
    isRelayPacsReport: true,
    isRelaySingleReport: true,
  });

  const [backupData, setBackupData] = useState<sendToPacsState>({
    region: '',
    isCrNmOverlay: true,
    isRelayCrImage: true,
    isRelayImage: true,
    isRelayLongitudinalReport: true,
    isRelayMrImage: true,
    isRelayPacsReport: true,
    isRelaySingleReport: true,
  });

  const isEditCondition = useMemo(() => _.isEqual(data, backupData), [data, backupData]);

  useCatchLeave(!isEditCondition);

  /**
   * 제품별 각각 다 들어갈 수 있는 모듈로 제품에 따라
   * api 경로 다르게 반환 하기 위한 함수
   */
  const getAPIUrl = () => {
    return `api/setting/nm/pacs`;
  };

  /**
   * 사용자 가 값 변경시 동작
   * @param key
   * @param value
   */

  const setDataValue = (key: keyof typeof data, value: boolean) => {
    setData((prevState) => {
      const clone = { ...prevState } as any;
      clone[key] = value;

      if (key === 'isRelayImage' && value === true) {
        clone['isRelayCrImage'] = true;
        clone['isRelayMrImage'] = true;
      }

      return clone;
    });
  };

  /**
   * 스위치 변경 컨펌
   * @param key
   * @param value
   */
  const confirmTurnOnOff = (key: keyof typeof data, value: boolean) => {
    const turn = value ? 'on' : 'off';

    //먼저 바꾸고 컨펌에 따라 결과 확정
    setDataValue(key, value);

    confirmTurnAutomaticallyOnOffDialog(
      turn,
      () => null,
      () => {
        setDataValue(key, !value);
      },
    );
  };

  /**
   * submit confirm cb
   * data 전송
   */
  const postDataCB = async () => {
    // valid 체크
    const url = getAPIUrl();
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: {
        ...data,
      },
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: `It has been reflected.`,
            footer: (
              <PrimaryButton
                onClick={async () => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setBackupData(data);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * get send to pacs data
   */
  const getData = async () => {
    const url = getAPIUrl(); // ok
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setData(r.result);
          setBackupData(r.result);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  useEffect(() => {
    (async () => {
      await getData();
    })();
  }, []);

  return (
    <>
      <label>
        Send to PACS
        <span
          style={{ display: 'inline-block', marginLeft: '20px', fontWeight: '400', fontSize: '12px', color: `#ff6767` }}
        >
          *Resend to PACS is not available when OFF.
        </span>
      </label>
      <StyledReportWrap>
        <SettingWrapper>
          <strong>Report</strong>
          <SettingBox>
            <SettingList>
              <li>
                <InputList>
                  <li className={'sub-title'}>Send automatically</li>
                </InputList>
                <InputList>
                  <span className={'send-off'}>Off</span>
                  <Switch
                    defaultValue={data.isRelayPacsReport}
                    onChangeValue={(e) => confirmTurnOnOff('isRelayPacsReport', e)}
                  />
                  <span>On</span>
                </InputList>
              </li>

              <li>
                <InputList>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'relaySingleReport'}
                      checked={data.isRelaySingleReport}
                      disabled={true}
                      onChange={(e) => setDataValue('isRelaySingleReport', e.target.checked)}
                    >
                      Single Report
                    </Input>
                  </li>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'relayLongitudinalReport'}
                      checked={data.isRelayLongitudinalReport}
                      disabled={!data.isRelayPacsReport}
                      onChange={(e) => setDataValue('isRelayLongitudinalReport', e.target.checked)}
                    >
                      Longitudinal Report
                    </Input>
                  </li>
                </InputList>
              </li>
            </SettingList>
          </SettingBox>
        </SettingWrapper>
        <SettingWrapper>
          <strong>Image</strong>
          <SettingBox>
            <SettingList>
              <li>
                <InputList>
                  <li className={'sub-title'}>Send automatically</li>
                </InputList>
                <InputList>
                  <InputList>
                    <span className={'send-off'}>Off</span>
                    <Switch
                      defaultValue={data.isRelayImage}
                      onChangeValue={(e) => confirmTurnOnOff('isRelayImage', e)}
                    />
                    <span>On</span>
                  </InputList>
                </InputList>
              </li>
            </SettingList>
          </SettingBox>
        </SettingWrapper>
        {data.isRelayImage && (
          <SettingWrapper>
            <strong>Image setting</strong>
            <SettingBox>
              <ReferenceBox>
                <div>Reference region</div>
                <div className="select-box">
                  <Select
                    list={[
                      {
                        label: 'Periaqueductal gray',
                        value: 'PAG',
                      },
                      {
                        label: 'Pons',
                        value: 'PONS',
                      },
                      {
                        label: 'All',
                        value: 'ALL',
                      },
                    ]}
                    selected={data.region}
                    synchronize
                    onSelected={(e) => setData((prevState) => ({ ...prevState, region: e.value }))}
                  />
                </div>
              </ReferenceBox>
              <InputList>
                <li>
                  <CheckboxLabel>
                    <Input
                      type={'checkbox'}
                      name="relayMrImage"
                      checked={data.isRelayMrImage}
                      onChange={(e) => {
                        if (!data.isRelayCrImage && !e.target.checked) {
                          setData((prevState) => {
                            const clone = JSON.parse(JSON.stringify(prevState));
                            clone['isRelayImage'] = false;
                            return clone;
                          });
                        }
                        setDataValue('isRelayMrImage', e.target.checked);
                      }}
                    />
                    <strong>MR + NM overlay</strong>
                  </CheckboxLabel>
                </li>
                <li>
                  <StyledImageSettingBox className={!data.isRelayCrImage ? 'none' : ''}>
                    <CheckboxLabel>
                      <Input
                        type={'checkbox'}
                        name={`relayCrImage`}
                        checked={data.isRelayCrImage}
                        onChange={(e) => {
                          if (!data.isRelayMrImage && !e.target.checked) {
                            setData((prevState) => {
                              const clone = JSON.parse(JSON.stringify(prevState));
                              clone['isRelayImage'] = false;
                              return clone;
                            });
                          }
                          setDataValue('isRelayCrImage', e.target.checked);
                        }}
                      />
                      <strong>CR Image</strong>
                    </CheckboxLabel>

                    <SwitchBox className={'overlay-box'}>
                      <span>NM overlay</span>
                      <Switch
                        defaultValue={data.isCrNmOverlay}
                        disabled={!data.isRelayCrImage}
                        onChangeValue={(e) => setDataValue('isCrNmOverlay', e)}
                      />
                    </SwitchBox>
                  </StyledImageSettingBox>
                </li>
              </InputList>
            </SettingBox>
          </SettingWrapper>
        )}
      </StyledReportWrap>
      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            font-size: 18px;
          `}
          onClick={submitDebounce(postDataCB)}
          disabled={isEditCondition}
        >
          Submit
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default NMSendToPacs;
